import { getLocalStorageItem, setLocalStorageItem } from '../utils/localStorageUtils';
/**
 * Location Override Service
 * Stores user-defined location names for specific coordinates
 */

interface LocationOverride {
  latitude: number;
  longitude: number;
  customName: string;
}

const STORAGE_KEY = 'hora_location_overrides';
const COORDINATE_PRECISION = 4; // ~11m precision

class LocationOverrideService {
  /**
   * Generate key from coordinates with fixed precision
   */
  private getCoordinateKey(lat: number, lng: number): string {
    const roundedLat = lat.toFixed(COORDINATE_PRECISION);
    const roundedLng = lng.toFixed(COORDINATE_PRECISION);
    return `${roundedLat},${roundedLng}`;
  }

  /**
   * Load all overrides from localStorage
   */
  private loadOverrides(): Map<string, string> {
    const data = getLocalStorageItem<Record<string, string>>(STORAGE_KEY, {});
    return new Map(Object.entries(data));
  }

  /**
   * Save all overrides to localStorage
   */
  private saveOverrides(overrides: Map<string, string>): void {
    setLocalStorageItem(STORAGE_KEY, Object.fromEntries(overrides));
  }

  /**
   * Get override for coordinates
   */
  getOverride(lat: number, lng: number): string | null {
    const key = this.getCoordinateKey(lat, lng);
    const overrides = this.loadOverrides();
    return overrides.get(key) || null;
  }

  /**
   * Set override for coordinates
   */
  setOverride(lat: number, lng: number, customName: string): void {
    const key = this.getCoordinateKey(lat, lng);
    const overrides = this.loadOverrides();
    overrides.set(key, customName);
    this.saveOverrides(overrides);
  }

  /**
   * Remove override for coordinates
   */
  removeOverride(lat: number, lng: number): void {
    const key = this.getCoordinateKey(lat, lng);
    const overrides = this.loadOverrides();
    overrides.delete(key);
    this.saveOverrides(overrides);
  }

  /**
   * Check if coordinates have an override
   */
  hasOverride(lat: number, lng: number): boolean {
    return this.getOverride(lat, lng) !== null;
  }

  /**
   * Clear all overrides
   */
  clearAll(): void {
    localStorage.removeItem(STORAGE_KEY);
  }
}

// Singleton instance
export const locationOverrideService = new LocationOverrideService();
